import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  chunk,
  isEmpty,
  isNull,
  isUndefined,
  keys,
  omit,
  uniqueId
} from 'lodash'
import Kpi from '../components/Kpi'
import { AmChartTypes } from '@ublique/components/Charts'
import useColorTheme from './useColorTheme'
import { ns, USER_FEEDBACK_STORE_KEY } from '../constants'
import { useStoreSelector } from '@ublique/platform'
import { STORE_KEY } from 'pages/ExtraOutput/constants'
import { selectors } from 'pages/ExtraOutput/state'
import { userFeedbackStore, userSelectors } from '../state'
import { fetchInfoData } from '../actions'
import useFetchData from 'pages/ExtraOutput/hooks/useFetchData'
import { updateState } from 'utils/state'

const { AMCHART_HORIZONTAL_STACKED } = AmChartTypes

function useKpisConfig() {
  const { t, i18n } = useTranslation('ExtraOutput')
  const colorTheme = useColorTheme()

  const filters = useStoreSelector(STORE_KEY, selectors.getFilters)
  const uom = useStoreSelector(USER_FEEDBACK_STORE_KEY, userSelectors.getUom)

  const fetchParams = useMemo(() => ({ ...filters, uom }), [filters, uom])

  const { data, refreshData /* , loading */ } = useFetchData(
    fetchInfoData,
    fetchParams
  )

  const referencePeriod = useMemo(() => {
    if (isEmpty(data)) return t('StoreCollaboration.LOADING_DATA')

    return `${data.referencePeriod.startDate} - ${data.referencePeriod.endDate}`
  }, [data, t])

  useEffect(() => {
    if (!isEmpty(data)) {
      updateState(userFeedbackStore, {
        isEditable: data?.referencePeriod?.editable
      })
    }
  }, [data])

  const kpisData = useMemo(() => formatData(data), [data])
  const chartData = useMemo(() => formatChartData(data), [data])

  //soglie per il grafico
  const thresholds = useMemo(() => formatThresholds(data), [data])

  const kpisText = useMemo(
    () =>
      kpisData.map((data) => ({
        config: { sm: 4, md: 8, lg: 5 },
        factory: 'KPIS',
        factoryConfig: {
          data,
          type: 'CUSTOM',
          noDataLabel: t('NO_DATA'),
          children: <Kpi data={data} t={t} />
        }
      })),
    [kpisData, t]
  )

  const kpisChart = useMemo(
    () => ({
      config: { sm: 4, md: 8, lg: 7 },
      factory: 'CHART',
      factoryConfig: {
        key: `chart-${uom}-${filters.store}-${
          isEmpty(filters.selectedRooms)
            ? 'all'
            : filters.selectedRooms.map((r) => r.id).join('-')
        }-${uniqueId()}`,
        type: AMCHART_HORIZONTAL_STACKED,
        color: colorTheme,
        theme: colorTheme.labelColor,
        size: 'sm',
        data: [chartData],
        onChartReady: (params) => onChartReady(params, thresholds, t),
        config: {
          title: 'cm³',
          yKey: 'category',
          scrollbar: true,
          disabledAnimations: true,
          series: getSeries(chartData)
        },
        legendLabels: translateKeys(getSeries(chartData), t),
        isLoading: isUndefined(chartData),
        isError: isNull(chartData),
        noDataLabel: t('NO_DATA'),
        language: i18n.language
      }
    }),
    [chartData, colorTheme, i18n.language, t, thresholds]
  )

  return {
    kpisConfig: [...kpisText, kpisChart],
    referencePeriod,
    refreshKpisData: refreshData
  }
}

export default useKpisConfig

const getSeries = (data) => {
  if (isEmpty(data) || isUndefined(data)) return []
  const chartKeys = keys(data)
  return chartKeys.filter((key) => key !== 'category' && key !== 'factory')
}

// divido l'obj in 2 obj
const formatData = (data) => {
  if (isEmpty(data)) return [data]

  const kpisData = data?.kpis
  const entries = Object.entries(kpisData)
  const chunks = chunk(entries, 7)

  return chunks.map((chunk) => Object.fromEntries(chunk))
}

const cleanData = (data) => {
  return omit(data, ['maximum', 'minimal'])
}

const formatChartData = (data) => {
  if (isEmpty(data)) return data
  const chartData = data?.chart

  const cleanedData = cleanData(chartData)
  return keys(cleanedData).reduce(
    (acc, key) => ({ ...acc, category: '', [key]: cleanedData[key] }),
    {}
  )
}
const formatThresholds = (data) => {
  if (isEmpty(data)) return []
  const chartData = data?.chart

  const cleanedData = omit(chartData, ['needs', 'notCovered'])
  return keys(cleanedData).reduce(
    (acc, key) => ({ ...acc, [key]: cleanedData[key] }),
    {}
  )
}

const translateKeys = (series, t) =>
  series.reduce(
    (acc, key) => ({
      ...acc,
      [key]: t(`${ns}.${key.toUpperCase()}`)
    }),
    {}
  )

const onChartReady = (chart, thresholds = {}, t) => {
  const valueAxis = chart.xAxes.getIndex(0)
  if (!valueAxis) return

  const updateAxisAndRanges = () => {
    const data = chart.data || []
    if (!data.length) return

    // ricavo le chiavi delle series dal primo elemento (escludo category/factory)
    const seriesKeys = Object.keys(data[0]).filter(
      (k) => k !== 'category' && k !== 'factory'
    )

    // calcolo il massimo delle somme per riga (stacked)
    let maxFromData = 0
    data.forEach((row) => {
      const sum = seriesKeys.reduce((s, k) => s + (Number(row[k]) || 0), 0)
      if (sum > maxFromData) maxFromData = sum
    })

    // valori soglie (forzo Number perché spesso arrivano come stringhe)
    const thrVals = Object.keys(thresholds || {}).map(
      (k) => Number(thresholds[k]) || 0
    )
    const maxThreshold = thrVals.length ? Math.max(...thrVals) : 0
    const minThreshold = thrVals.length ? Math.min(...thrVals) : 0

    // desiderato: includere sia dati che soglie
    let desiredMax = Math.max(maxFromData, maxThreshold)
    let desiredMin = Math.min(0, minThreshold) // di solito vogliamo includere 0

    // piccolo padding per non incastrare linee sui bordi
    const span = desiredMax - desiredMin || 1
    const pad = span * 0.05
    desiredMax += pad
    desiredMin -= pad

    // forza l'asse a rispettare min/max
    valueAxis.strictMinMax = true
    valueAxis.min = desiredMin
    valueAxis.max = desiredMax

    // rimuovo eventuali axisRanges preesistenti (evita duplicati)
    while (valueAxis.axisRanges.length) {
      valueAxis.axisRanges.removeIndex(0)
    }

    // ricreo le range/soglie
    Object.keys(thresholds || {}).forEach((key, i) => {
      const range = valueAxis.axisRanges.create()
      range.value = Number(thresholds[key])
      range.grid.stroke = '#FFFFFF'
      range.grid.strokeWidth = 2
      range.grid.strokeOpacity = 1

      range.label.text = t(`${ns}.${key.toUpperCase()}`)
      range.label.fill = '#FFFFFF'
      range.label.fontSize = 12
      range.label.rotation = 0
      range.label.horizontalCenter = i === 0 ? 'right' : 'left'
      range.label.verticalCenter = 'top'
      range.label.location = 0
      range.label.inside = false
      range.label.padding(4, 4, 4, 4)

      range.label.dx = 0
      range.label.dy = i === 0 ? -90 : -100
    })
  }

  // se i dati sono già stati caricati -> aggiorno subito, altrimenti aspetto datavalidated
  if (chart.data && chart.data.length) {
    updateAxisAndRanges()
  } else {
    chart.events.on('datavalidated', updateAxisAndRanges)
  }
}
